import type {
  IChartApi,
  IPrimitivePaneRenderer,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';

type RenderTarget = Parameters<NonNullable<IPrimitivePaneRenderer['drawBackground']>>[0];

export interface KnotLine {
  /** Stored time of the candle that formed it. */
  time: number;
  level: number;
  /** Half-width of the accepting band, in price. */
  tolerance: number;
  direction: 'up' | 'down' | 'flat';
  /** Stored time of the close that took it, or null while live. */
  hitTime: number | null;
  /**
   * Stored time of a close sitting on the knot that is still waiting for the
   * next candle to confirm it, or null. Drawn dashed.
   */
  pendingTime: number | null;
}

export interface KnotColors {
  /** The most recently taken knot, singled out from the rest. */
  latest: string;
  /** Lightness for the generated hues, tuned to the chart background. */
  lightness: number;
  /** Knots waiting on a confirming candle. */
  pending: string;
}

const FALLBACK: KnotColors = { latest: '#f59e0b', lightness: 62, pending: '#38bdf8' };

/**
 * A distinct hue per knot, stepped by the golden angle.
 *
 * 137.5 degrees is the step that keeps successive values as far apart as
 * possible however many there are, so neighbouring knots never come out in
 * near-identical colours the way a fixed palette cycled modulo-N would.
 */
function hueFor(index: number, lightness: number): string {
  return `hsl(${((index * 137.508) % 360).toFixed(1)} 72% ${lightness}%)`;
}

/**
 * Draws knot levels as horizontal rays running forward from their candle.
 *
 * Each line stops at the close that took it — drawing it onward would claim the
 * level is still in play when the whole point is that it has been used up.
 *
 * Only TAKEN knots are drawn. A knot nothing has closed on is a level that has
 * not proved anything yet, and drawing every one of them buried the ones that
 * had in a field of hundreds of lines.
 *
 * Each gets its own hue so a level can be followed back to the candle that made
 * it, and the most recently taken one is orange — it is the level price has just
 * finished with, so it is the one worth finding first.
 *
 * A PENDING knot — closed on, but its confirming candle still open — is drawn
 * dashed, in its own colour, running to the right edge: it is not used up yet,
 * and may yet be dropped if the next candle closes the wrong colour.
 */
export class KnotPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: ISeriesApi<SeriesType> | null = null;
  private requestUpdate: (() => void) | null = null;
  private knots: KnotLine[] = [];
  private showBands = true;
  private colors: KnotColors = FALLBACK;
  /** Taken and pending knots, in the order they were consumed, each with its colour. */
  private drawn: (KnotLine & { color: string; latest: boolean; pending: boolean })[] = [];
  private readonly view: IPrimitivePaneView;

  constructor(knots: KnotLine[] = []) {
    this.knots = knots;

    const renderer: IPrimitivePaneRenderer = {
      drawBackground: (target: RenderTarget) => this.render(target),
      draw: () => {},
    };

    this.view = {
      zOrder: (): PrimitivePaneViewZOrder => 'bottom',
      renderer: () => renderer,
    };
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart as IChartApi;
    this.series = param.series as ISeriesApi<SeriesType>;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  setKnots(knots: KnotLine[]): void {
    this.knots = knots;
    this.rebuild();
    this.requestUpdate?.();
  }

  setColors(colors: KnotColors): void {
    this.colors = colors;
    this.rebuild();
    this.requestUpdate?.();
  }

  /*
   * Built when the data or the palette changes, not per frame: the renderer runs
   * on every pan and zoom, and none of this depends on the viewport.
   */
  private rebuild(): void {
    const taken = this.knots
      .filter((k) => k.hitTime !== null)
      .sort((a, b) => a.hitTime! - b.hitTime!);

    this.drawn = taken.map((k, i) => ({
      ...k,
      // Hue follows the order knots were CONSUMED, so colour reads as sequence.
      color: i === taken.length - 1 ? this.colors.latest : hueFor(i, this.colors.lightness),
      latest: i === taken.length - 1,
      pending: false,
    }));

    // Drawn last, so a pending level sits on top of the taken ones.
    for (const k of this.knots) {
      if (k.hitTime === null && k.pendingTime !== null) {
        this.drawn.push({ ...k, color: this.colors.pending, latest: true, pending: true });
      }
    }
  }

  setShowBands(show: boolean): void {
    this.showBands = show;
    this.requestUpdate?.();
  }

  private render(target: RenderTarget): void {
    const chart = this.chart;
    const series = this.series;
    if (!chart || !series || this.drawn.length === 0) return;

    const timeScale = chart.timeScale();

    /*
     * What is on screen, in stored time, fetched once per frame.
     *
     * The pixel test further down catches everything this does, but only after
     * a priceToCoordinate and up to three timeToCoordinate calls per knot. With
     * a deep window there are thousands of knots and nearly all of them are off
     * screen at any zoom, so comparing timestamps first keeps the cost of a pan
     * proportional to what is actually visible rather than to the whole history.
     */
    const visible = timeScale.getVisibleRange();
    const from = visible === null ? null : (visible.from as number);
    const to = visible === null ? null : (visible.to as number);

    target.useBitmapCoordinateSpace(({ context, bitmapSize, horizontalPixelRatio, verticalPixelRatio }) => {
      for (const knot of this.drawn) {
        if (from !== null && to !== null) {
          // A pending knot runs to the right edge, so only its start can rule
          // it out; a taken one ends at the close that took it.
          const ends = knot.pending ? Infinity : (knot.hitTime as number);
          if (ends < from || knot.time > to) continue;
        }

        const y = series.priceToCoordinate(knot.level);
        if (y === null) continue;

        const x1 = timeScale.timeToCoordinate(knot.time as UTCTimestamp);
        if (x1 === null) continue;

        // A pending knot is still live, so it runs to the right edge; its tick
        // marks the close that is waiting on confirmation.
        const x2 = knot.pending ? null : timeScale.timeToCoordinate(knot.hitTime as UTCTimestamp);
        const xTick = knot.pending
          ? timeScale.timeToCoordinate(knot.pendingTime as UTCTimestamp)
          : x2;

        const left = x1 * horizontalPixelRatio;
        // Scrolled off the right: run to the edge rather than dropping the line.
        const right = x2 === null ? bitmapSize.width : x2 * horizontalPixelRatio;
        if (right < 0 || left > bitmapSize.width) continue;

        const py = Math.round(y * verticalPixelRatio) + 0.5;

        // The accepting band, so it is visible how close a close has to land.
        if (this.showBands) {
          const yTop = series.priceToCoordinate(knot.level + knot.tolerance);
          const yBottom = series.priceToCoordinate(knot.level - knot.tolerance);
          if (yTop !== null && yBottom !== null) {
            context.globalAlpha = 0.12;
            context.fillStyle = knot.color;
            context.fillRect(
              left,
              yTop * verticalPixelRatio,
              right - left,
              Math.max(1, (yBottom - yTop) * verticalPixelRatio),
            );
            context.globalAlpha = 1;
          }
        }

        context.save();
        context.strokeStyle = knot.color;
        context.globalAlpha = knot.latest ? 1 : 0.8;
        // The newest taken knot is drawn thicker as well as coloured, so it is
        // still findable for anyone who cannot separate it by hue.
        context.lineWidth = Math.max(1, Math.round((knot.latest ? 2 : 1) * horizontalPixelRatio));
        if (knot.pending) {
          const dash = 6 * horizontalPixelRatio;
          context.setLineDash([dash, dash * 0.7]);
        }
        context.beginPath();
        context.moveTo(left, py);
        context.lineTo(right, py);
        context.stroke();
        context.setLineDash([]);

        // A tick at the close that consumed it (or, when pending, the close
        // waiting on it), marking where the level was used.
        if (xTick !== null) {
          const tx = xTick * horizontalPixelRatio;
          context.globalAlpha = knot.latest ? 1 : 0.7;
          context.beginPath();
          context.moveTo(tx, py - 3 * verticalPixelRatio);
          context.lineTo(tx, py + 3 * verticalPixelRatio);
          context.stroke();
        }
        context.restore();
      }
    });
  }
}
